import React, { useCallback, useEffect, useState } from 'react';

import { EsteiraTagValorItem, getCarTagValores } from '../../api/esteiraTagsApi';
import { useEsteiras } from '../../hooks/useEsteiras';
import { notify } from '../../shared/feedback/notify';
import './TagsDaEsteira.css';

interface TagsDaEsteiraProps {
  carId: string;
  /** Esteira atual do veículo — aceita id ou chave; a normalização é feita aqui. */
  esteiraAtual?: string | null;
  /** Quando informado, avisa o hospedeiro de que há (ou não) motivo preenchido. */
  onCarregado?: (temMotivoPreenchido: boolean) => void;
  /** Recarrega quando o hospedeiro salvar (basta trocar o número). */
  refreshKey?: number;
  /**
   * `inline` renderiza como selo compacto, para ficar AO LADO DA PLACA no cabeçalho do modal
   * (decisão do Victor, 03/09/2026) — é a primeira coisa que se lê ao abrir o veículo. `bloco`
   * é a faixa completa, para quem precisa do rótulo por extenso.
   */
  variante?: 'bloco' | 'inline';
}

/**
 * Motivo / tags da ESTEIRA ATUAL do veículo, em destaque (at-89, 03/09/2026).
 *
 * Por que existe: as tags só eram preenchidas no cadastro e na edição do veículo, e "passavam
 * muito batido" (Victor, 03/09/2026) — ninguém via o motivo pelo qual o veículo estava parado.
 * Agora ele aparece no modal do veículo, junto do resto.
 *
 * 🔴 SÓ A ESTEIRA ATUAL, e isso é a razão de o componente existir: cada esteira tem a sua
 * "Motivo da pendência", e sem o recorte a tela mostraria seis campos com o mesmo nome. Desde a
 * at-134 o recorte é do BACKEND (`esteiraId` na rota) — o componente manda o id da esteira atual
 * e não filtra nada localmente, para não esconder uma regressão da rota.
 *
 * ⚠️ Motivo NÃO preenchido é dito por extenso, não escondido: é exatamente o esquecimento que
 * esta entrega existe para tornar visível.
 */
const TagsDaEsteira: React.FC<TagsDaEsteiraProps> = ({
  carId,
  esteiraAtual,
  onCarregado,
  refreshKey,
  variante = 'bloco',
}) => {
  const { idDe, labelDe, isFetched: esteirasCarregadas, isError: esteirasFalharam } = useEsteiras();
  const [tags, setTags] = useState<EsteiraTagValorItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [falhou, setFalhou] = useState(false);

  const esteiraId = idDe(esteiraAtual);

  const carregar = useCallback(async () => {
    if (!carId) return;
    if (!esteiraId) {
      // Sem a lista de esteiras não há como saber o id da atual: esperar por ela, e tratar a
      // falha dela como falha (nunca como "sem motivo").
      if (esteirasFalharam) {
        setTags([]);
        setFalhou(true);
        setCarregando(false);
      } else if (esteirasCarregadas || !esteiraAtual) {
        setTags([]);
        setFalhou(false);
        setCarregando(false);
      }
      return;
    }
    setCarregando(true);
    try {
      const daEsteira = await getCarTagValores(carId, esteiraId);
      setTags(daEsteira);
      setFalhou(false);
    } catch (error) {
      setTags([]);
      setFalhou(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar o motivo da esteira. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [carId, esteiraId, esteiraAtual, esteirasCarregadas, esteirasFalharam]);

  useEffect(() => {
    void carregar();
  }, [carregar, refreshKey]);

  const daEsteiraAtual = tags;

  useEffect(() => {
    if (carregando || falhou) return;
    onCarregado?.(tags.some((t) => Boolean(t.valor)));
    // `onCarregado` vem do hospedeiro, sem memo: depender dele reentraria em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregando, falhou, tags]);

  if (carregando) return null;

  if (falhou) {
    // ⚠️ Também no selo: falha de carga não pode virar "sem motivo" (at-60).
    return variante === 'inline' ? (
      <span
        className="tags-da-esteira-selo erro"
        title="Não foi possível verificar o motivo desta etapa."
      >
        Motivo não verificado
      </span>
    ) : (
      <div className="tags-da-esteira">
        <p className="tags-da-esteira-erro">
          Não foi possível verificar o motivo desta etapa. Confira antes de informar o associado.
        </p>
      </div>
    );
  }

  // A esteira atual não tem tag cadastrada: nada a mostrar (bootstrap inerte).
  if (daEsteiraAtual.length === 0) return null;

  if (variante === 'inline') {
    return (
      <>
        {daEsteiraAtual.map((tag) => {
          const rotulo = tag.opcoes.find((o) => o.valor === tag.valor)?.label ?? tag.valor;
          // Esteira + motivo no mesmo selo (Victor, 03/09/2026): "Sindicância - Aguardando
          // definição". O motivo sozinho não diz de qual etapa ele fala, e várias esteiras têm
          // motivos de mesmo nome ("Aguardando documentação" existe em quatro delas).
          const nomeEsteira = labelDe(tag.esteiraId);

          return (
            <span
              key={tag.definicaoId}
              className={`tags-da-esteira-selo ${rotulo ? '' : 'vazio'}`}
              title={`${nomeEsteira} — ${tag.nome}: ${rotulo || 'não informado'}`}
            >
              {nomeEsteira} - {rotulo || 'Motivo não informado'}
            </span>
          );
        })}
      </>
    );
  }

  return (
    <div className="tags-da-esteira">
      {daEsteiraAtual.map((tag) => {
        const rotulo = tag.opcoes.find((o) => o.valor === tag.valor)?.label ?? tag.valor;

        return (
          <div key={tag.definicaoId} className="tags-da-esteira-item">
            <span className="tags-da-esteira-label">{tag.nome}</span>
            {rotulo ? (
              <span className="tags-da-esteira-valor" title={rotulo}>
                {rotulo}
              </span>
            ) : (
              <span className="tags-da-esteira-vazio">Não informado</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TagsDaEsteira;
